import { Link } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { formatMYR, calculateProgress, daysRemaining } from '../../lib/api';
import { Calendar, Users, User, Receipt } from 'lucide-react';

const statusColors = {
  PENDING_REVIEW: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  APPROVED: 'bg-green-100 text-green-800 border-green-200',
  REJECTED: 'bg-red-100 text-red-800 border-red-200',
  ENDED: 'bg-gray-100 text-gray-800 border-gray-200',
};

const statusLabels = {
  PENDING_REVIEW: 'Pending Review',
  APPROVED: 'Active',
  REJECTED: 'Rejected',
  ENDED: 'Ended',
};

export const CampaignCard = ({ campaign, showStatus = false }) => {
  const progress = calculateProgress(campaign.raised_cents, campaign.target_cents);
  const days = daysRemaining(campaign.deadline_date);
  const isEnded = campaign.status === 'ENDED' || campaign.ended_at || days === 0;

  const defaultImage = 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=800&q=80';

  return (
    <Link to={`/campaigns/${campaign.id}`} data-testid={`campaign-card-${campaign.id}`}>
      <Card className="overflow-hidden card-hover bg-white border-border/50">
        <div className="relative aspect-[16/10] overflow-hidden">
          <img
            src={campaign.image_url || defaultImage}
            alt={campaign.title}
            className="w-full h-full object-cover transition-transform duration-500 hover:scale-105"
            onError={(e) => {
              e.target.src = defaultImage;
            }}
          />
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {showStatus && (
              <Badge className={`${statusColors[campaign.status]} border`}>
                {statusLabels[campaign.status]}
              </Badge>
            )}
            {campaign.is_tax_deductible && (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 border">
                <Receipt className="w-3 h-3 mr-1" />
                Tax Deductible
              </Badge>
            )}
          </div>
          {isEnded && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <span className="text-white font-bold text-lg">Campaign Ended</span>
            </div>
          )}
        </div>
        <CardContent className="p-5">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground mb-2">
            <User className="w-3.5 h-3.5" />
            <span>by {campaign.owner_username || 'Anonymous'}</span>
          </div>
          <h3 className="font-semibold text-lg text-foreground line-clamp-2 mb-2 leading-tight">
            {campaign.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
            {campaign.description}
          </p>

          {/* Progress */}
          <div className="space-y-2 mb-4">
            <Progress value={progress} className="h-2" />
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-primary">
                {formatMYR(campaign.raised_cents)}
              </span>
              <span className="text-muted-foreground">
                of {formatMYR(campaign.target_cents)}
              </span>
            </div>
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1.5">
              <Users className="w-4 h-4" />
              <span>{campaign.donation_count} donors</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{isEnded ? 'Ended' : `${days} days left`}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};
